/**
 * Proactive stale-bundle detection (vingreact#106 follow-up / #261).
 *
 * The reactive guards already cover two vectors: a bundle sending removed query
 * params (`staleBundle.ts`, the `unknown_params` 400) and a cached index.html
 * that can't find a lazy chunk (`installPreloadErrorReload`). Neither catches a
 * FUNCTIONAL-but-behind bundle — one that keeps running old code across a
 * deploy, most visibly bouncing the `/sso` login exchange. This detects that
 * proactively by comparing the entry-script hash THIS tab is running against
 * the one the server is currently serving.
 *
 * Detection only reports a newer deploy; acting on it (a full page load on the
 * next navigation, once per build per tab) is `useDeployedBuildReload`. Every
 * failure mode (fetch error, unparseable HTML, dev server with no hashed entry)
 * resolves to "no update".
 */

/** Match a Vite hashed entry: `/assets/index-<hash>.js`. */
const ENTRY_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

/**
 * The entry-script URL this tab actually loaded, read from the live DOM.
 * `null` when there is no hashed entry — the Vite dev server serves
 * `/src/main.tsx`, so the check is inert in development and in tests.
 */
export function currentEntryHash(doc: Document = document): string | null {
  const scripts = Array.from(doc.querySelectorAll('script[type="module"][src]'));
  for (const s of scripts) {
    const src = s.getAttribute('src') ?? '';
    const m = src.match(ENTRY_RE);
    if (m) return m[0];
  }
  return null;
}

/** Extract the hashed entry from an index.html string, or `null`. */
export function parseEntryHash(html: string): string | null {
  const m = html.match(ENTRY_RE);
  return m ? m[0] : null;
}

/**
 * The entry-script the server is serving right now. Fetches index.html with
 * `cache: 'no-store'` (prod already sends it `no-cache`, but we belt-and-brace
 * against an intermediary). Returns `null` on ANY failure — a transient network
 * blip must never surface as a phantom "update available".
 */
export async function fetchDeployedEntryHash(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const res = await fetchImpl(`/index.html?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    return parseEntryHash(await res.text());
  } catch {
    return null;
  }
}

/**
 * True only when both hashes are known and differ — i.e. the server is serving
 * a build this tab is not running. Unknown on either side ⇒ false (no nag).
 */
export function isNewerDeploy(current: string | null, deployed: string | null): boolean {
  if (!current || !deployed) return false;
  return current !== deployed;
}

/**
 * At most one self-reload per deployed build per tab (Q-506 card 1573): the
 * mark is a sessionStorage key on the deployed entry hash, so a bad signal
 * (a CDN serving two builds, say) cannot loop. Storage that throws (private
 * mode) means we cannot prove we have not reloaded yet, so the answer is "no
 * reload": fail safe, never loop.
 */
const RELOADED_PREFIX = 'ving:deploy-reloaded:';

/** True only when storage is readable and this tab has not yet reloaded for the build. */
export function canReloadForBuild(deployedHash: string): boolean {
  try {
    return sessionStorage.getItem(RELOADED_PREFIX + deployedHash) === null;
  } catch {
    return false;
  }
}

/** Record the reload. Returns false when the mark could not be stored (then do not reload). */
export function markReloadedForBuild(deployedHash: string): boolean {
  try {
    sessionStorage.setItem(RELOADED_PREFIX + deployedHash, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/** Test hygiene: drop every reload mark. */
export function clearReloadMarks(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(RELOADED_PREFIX)) keys.push(key);
    }
    for (const key of keys) sessionStorage.removeItem(key);
  } catch {
    // Nothing to clear.
  }
}
